/* eslint-disable max-depth */
import React, { Dispatch, PropsWithChildren, ReactElement, useEffect, useState, useRef, useCallback, SetStateAction } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './DebitCardFunding.css';
import cardProviders from '../../assets/images/card-providers-logos.png';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import { cleanInternalLinkUrl, getApiToken, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import SessionStorage from '../../utils/SessionStorage';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { AcquisitionFlow, checkIfInValidAcquisitionFlow, getAcquisitionFlow, getIpAddress } from 'utils/acquisition-utils';
import { Button, Row, Col, Title, Image, Section, Label  } from '../../importCL';
import { UUIDv4 } from '../../utils/cob-utils'
import { callAcquisitionBff } from '../../utils/acquisition-api/AcquisitionBff';
import { TFieldState } from '../../providers/ModernAcquisitionProvider/types';
import { TFundingModuleVariants } from '../FundingModule/FundingModule';

interface IDebitCardFundingProps extends MappedComponentProperties {
    author?: boolean;
    title?: string;
    instructionText?: string;
    nameLabel?: string;
    cardNumberLabel?: string;
    cardExpirationLabel?: string;
    cardSecurityLabel?: string;
    stateLabel?: string;
    zipLabel?: string;
    submitButtonText?: string;
    nextPagePath?: string;
    previousPagePath?: string;
    debitCardFailedPagePath?: string;
    verificationErrorCode?: string;
    processingErrorCode?: string;
    fundingErrorCode?: string;
    attemptsExceededRedirect?: string;
    unprocessableEntityErrorCode?: string;
    fundingApprovedRedirect?: string;
    fundingDeclinedRedirect?: string;
    fundingErrorRedirect?: string;

    // DEBIT ON FUNDING AB TEST---------------------------------------------
    singlePageFunding?: boolean;
    setDebitHasErrors?: Dispatch<SetStateAction<boolean>>;
    isFundingSubmitting?: React.MutableRefObject<boolean>;
    setIsSpinning?: Dispatch<SetStateAction<boolean>>;
    variant?: TFundingModuleVariants;

}
interface AciErrorObject {
    kind: string;
    paymentMethod: string,
    message: {
        code: string;
        default: string;
    };
}
interface IAuthenticateAciResponse {
    access_token: string,
    billing_account_id: string,
    expired_in: number,
    token_type: string
}

interface IAciAuthentication {
    accessToken: string,
    billingAccountId: string,
}

type TFieldKey = 'account-holder-name' | 'account-region-code' | 'account-postal-code' | 'card-number' | 'expiration-date' | 'security-code';
interface IFieldState {
    name: string;
    status: TFieldState; 
    errorMessage: string | null; 

};
type TField = Record<TFieldKey, IFieldState>;

interface IOnValidateEvent {
    kind: string;
    paymentMethod: string;
    message: {
        code: string;
        default: string;
    }
    details: { // array of objects
        kind: string;
        target: TFieldKey;
        message?: {
            code: string;
            default: string;
            data?: {
                cardKind: string;
                brandName: string;
                paymentMethod: string;
                paymentMethodBrand: string;
            }
        }
    }[]
}

export interface IAciFundingAttempt {
    achAttemptsExceeded: boolean;
    debitAttemptsExceeded: boolean;
    responseMetadata: {
        correlationId: string;
        responseDateTime: number;
    }
}

export const DebitCardFundingEditConfig = {
    emptyLabel: 'Debit Card Funding',
    isEmpty: (props: PropsWithChildren<IDebitCardFundingProps>) => {
        return !props || !props.title;
    },
};

const DebitCardFunding = (props: PropsWithChildren<IDebitCardFundingProps>): JSX.Element => {
    const { showPushdown, closePushdown } = usePushdownContext();

    const aciScriptElementRef = useRef(null);
    const aciAuthenticationRef = useRef<IAciAuthentication>({ accessToken: '', billingAccountId: '' });
    const isSubmitting = useRef(false);

    const attempts = useRef(0);
    const [isSpinning, setIsSpinning] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);
    const fields = useRef<TField>({
        'account-holder-name': { name: 'Name on Card', status: 'neutral', errorMessage: null },
        'account-region-code': { name: 'State', status: 'neutral', errorMessage: null },
        'account-postal-code': { name: 'Zip Code', status: 'neutral', errorMessage: null },
        'card-number': { name: 'Card Number', status: 'neutral', errorMessage: null },
        'expiration-date': { name: 'Card Expiration Date', status: 'neutral', errorMessage: null },
        'security-code': { name: 'Security Code', status: 'neutral', errorMessage: null },
    });

    // const isDisabled = isSubmitting.current || Object.values(fields.current).some(f => f.status !== 'success');
    const isProduction = window.location.hostname.includes('creditonebank');
    const SUBMIT_ERROR_MESSAGES = {
        'BlockedBin': 'This type of credit card is not accepted. Please try another credit card.',
        'CardExpired': 'The card provided has expired',
        'CardDeclined': 'The card provided was declined',
    }

    const submitOnClick = (event) => {
        if (isSubmitting.current) {
            return;
        }
        // immediately disable the button without waiting for state update
        event.currentTarget.disabled = true;
        isSubmitting.current = true;
        // legacy attempt counter
        setIsSpinning(true);
    }

    const redirect = useCallback((
        failReasonCode: string | boolean = '',
        redirectTo: string = props.previousPagePath,
    ) => {
        const params = new URLSearchParams(window.location.search);
        if (failReasonCode) {
            params.append('fundVerify', 'failed');
            if (failReasonCode !== true) {
                params.append('errorCode', failReasonCode);
            }
        }
        if (redirectTo?.indexOf('?') > -1) {
            redirectTo = redirectTo.slice(0, redirectTo.indexOf('?'));
        }
        if (redirectTo) {
            window.location.assign(cleanInternalLinkUrl(redirectTo) + (params.toString().length ? '?' + params.toString() : ''));
        }
    }, [props]);

    const checkForPushdowns = () => {
        const errorMessages = Object.values(fields.current).map(f => f.errorMessage).filter(Boolean);
        const hasFieldPushdownError = errorMessages.length > 0;

        if (hasFieldPushdownError) {
            const errorObj = {
                header: 'Problem Processing Your Request',
                message: errorMessages,
                description: 'The following fields and/or sections require your attention:',
                pushdownCode: 0,
                color: 'error',
            };
            showPushdown(errorObj);
            setIsDisabled(true);
            if (props.singlePageFunding) {
                props.setDebitHasErrors(true)
            }
        } else {
            closePushdown();
        }
    }

    const createFundingRequestBody = async () => {
        // funding project from app status
        const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', null);
        if (flowApplicationData && flowApplicationData?.DebitFundingInformation?.FundToken) {
            return {
                reservationNumber: sessionStorage.getItem('ReservationNumber') || flowApplicationData?.reservationNumber || flowApplicationData?.ReservationNumber || '',
                funding: {
                    debitCard: {
                        debitFundingTokenId: flowApplicationData.DebitFundingInformation.FundToken,
                        debitFundGuid: flowApplicationData.DebitFundingInformation.FundGuid,
                    },
                    amountToFund: flowApplicationData.DebitFundingInformation.AmountToFund,
                },
                partnerData: {
                    partnerId: 'AEM',
                },
                requestMetadata: {
                    correlationId: UUIDv4(),
                    requestDateTime: Math.floor(new Date().getTime() / 1000),
                    requestedBy: 'AEM',
                    channel: 'WEB',
                    ipAddress: await getIpAddress(),
                },
            };
        }
        
        // normal flow
        const pqFullAppData = await SessionStorage.tryGetCipher('prequalFullAppData', {});
        return {
            reservationNumber: sessionStorage.getItem('ReservationNumber') || '',
            funding: {
                debitCard: {
                    debitFundingTokenId: pqFullAppData.DebitFundingInformation.FundToken,
                    debitFundGuid: pqFullAppData.DebitFundingInformation.FundGuid,
                },
                amountToFund: pqFullAppData.DebitFundingInformation.AmountToFund,
            },
            partnerData: {
                partnerId: 'AEM',
            },
            requestMetadata: {
                correlationId: UUIDv4(),
                requestDateTime: Math.floor(new Date().getTime() / 1000),
                requestedBy: 'AEM',
                channel: 'WEB',
                ipAddress: await getIpAddress(),
            },
        };
        
    };

    useEffect(() => {
        if (window.location.search.indexOf('fundVerify=failed') > -1) {
            // Assume they've exausted all options
            redirect(false, props.attemptsExceededRedirect);
            return;
        }

        let isApplicationRedirecting = false;
        const src = isProduction 
            ? 'https://cds.officialpayments.com/js-sdk/1.7.0/speedpay.js?billerId=e0926205-63ba-405e-a425-f4c505b6083a'
            : 'https://sandbox-cds.officialpayments.com/js-sdk/1.7.0/speedpay.js?billerId=e0926205-63ba-405e-a425-f4c505b6083a';

        // The callback triggered when the funding account is created successfully.
        const onCreateToken = async (event: any) => {
            // redirect to pending-decision since there are no ACH attempts at this point
            // user can technically retry ACI from application-status if the app is not declined
            // and they still have ACI attempts
            if (!event?.token?.id) {
                redirect(props.verificationErrorCode, props.fundingErrorRedirect);
            }

            let fundTokenId = await SessionStorage.tryGetCipher('aciFundTokenId', '');
            if (!fundTokenId) {
                SessionStorage.setCipherWithCurrentScope('aciFundTokenId', event.token.id);
                fundTokenId = event.token.id;
            }

            if (SessionStorage.verifyNotEmpty('flowApplicationData')) {
                const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', {});
                flowApplicationData.DebitFundingInformation = {
                    ...flowApplicationData.DebitFundingInformation,
                    FundToken: fundTokenId,
                    FundGuid: aciAuthenticationRef.current.billingAccountId,
                };
                SessionStorage.setObjectWithCurrentScope('flowApplicationData', flowApplicationData);

            } else { // old flow, bypass submit until full application submit
                const fullAppInfo = await SessionStorage.tryGetCipher('prequalFullAppData', {});
                fullAppInfo.DebitFundingInformation = {
                    ...fullAppInfo.DebitFundingInformation,
                    CardLastFour: event.token.truncatedCardNumber,
                    FundToken: fundTokenId,
                    CardType: event.token.brandKind,
                    IsSingleUse: event.token.singleUse,
                    NickName: event.token.name,
                    FundGuid: aciAuthenticationRef.current.billingAccountId,
                };

                const dateStr = event.token.expirationDate;
                const month = dateStr.substring(0, 2);
                const year = dateStr.substring(2, 4);
                const expDate = new Date(parseInt('20' + year), parseInt(month) - 1);
                fullAppInfo.DebitFundingInformation.CardExpirationDate = expDate.toISOString();
                await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', fullAppInfo);
            }

            if (
                checkIfInValidAcquisitionFlow([
                    AcquisitionFlow.PQ,
                    AcquisitionFlow.PHA
                ])
            ) {
                const formattedData = await createFundingRequestBody();
                let isApplicationRedirecting = true;

                await callAcquisitionBff('secured-card-funding', {
                    replace: { 'applicationId': SessionStorage.get('ApplicationId') },
                    body: formattedData,
                }).then(async (response: any) => {
                    if (!response) {
                        throw { response };
                    }

                    const responseData = response.data;
                    SessionStorage.setObjectWithCurrentScope('fundingResponse', responseData);
                    const parsedStatusDescription = responseData.applicationStatus.statusDescription.toUpperCase().replaceAll('_', ' ');

                    if (responseData.applicationStatus.applicationStatus === 'Approved' && parsedStatusDescription === 'READY TO BOARD') {
                        redirect(false, props.fundingApprovedRedirect)

                    } else if (responseData.applicationStatus.applicationStatus === 'Declined') {
                        redirect(false, props.fundingDeclinedRedirect)

                    } else if (responseData.applicationStatus.applicationStatus === 'Ready To Fund') {
                        if (parsedStatusDescription === 'FAILED FUNDING NSF' || parsedStatusDescription === 'FAILED DEBIT FUNDING VERIFICATION') {
                            showPushdown(props.fundingErrorCode as string);
                            isApplicationRedirecting = false;
                        } else {
                            // unhandled/unknown response 
                            redirect(false, props.fundingErrorRedirect);
                        }
                    } else if (responseData.applicationStatus.applicationStatus === 'Error' && parsedStatusDescription === 'REQUEST FAILED INTERNAL ERROR') {
                        redirect(false, props.fundingErrorRedirect)
                    }
                }).catch((err) => {
                    // redirect to pending-decision since there are no ACH attempts at this point
                    // user can technically retry ACI from application-status if the app is not declined
                    // and they still have ACI attempts
                    console.error(err);
                    redirect(false, props.fundingErrorRedirect);
                }).finally(() => {
                    isSubmitting.current = false;
                    if (props.singlePageFunding) {
                        props.isFundingSubmitting.current = false;
                    }
                    if (!isApplicationRedirecting) {
                        setIsSpinning(false);
                        // ensure button is not enabled during redirect
                        setIsDisabled(true);
                        if (props.singlePageFunding) {
                            props.setDebitHasErrors(true);
                        }
                    }
                    // return to form with pushdown but enable button since
                    // fields are valid
                    setIsDisabled(false);
                    if (props.singlePageFunding) {
                        props.setDebitHasErrors(false);
                    }
                });
            } else {
                redirect(false, props.nextPagePath);
            }
        };

        const onValidate = (event: IOnValidateEvent) => {
            const fieldsCopy: TField = JSON.parse(JSON.stringify(fields.current));
            for (const detail of event.details) {
                if (!Object.keys(fields.current).includes(detail.target)) {
                    continue;
                }

                if (detail.kind === 'Valid') {
                    fieldsCopy[detail.target].status = 'success';
                    fieldsCopy[detail.target].errorMessage = null;
                    continue;
                }

                let errorMessage = `Please provide a valid ${fields.current[detail.target].name}`;
                if (fields.current[detail.target].name === 'Card Number' && detail.kind in SUBMIT_ERROR_MESSAGES) {
                    errorMessage = SUBMIT_ERROR_MESSAGES[detail.kind]
                }
                fieldsCopy[detail.target].status = 'error';
                fieldsCopy[detail.target].errorMessage = errorMessage;
            }

            // any fields in error will cause disabled when recalculated on render
            fields.current = fieldsCopy;
            const hasFieldError = Object.values(fieldsCopy).some(f => f.status !== 'success');
            setIsDisabled(hasFieldError);
            if (props.singlePageFunding) {
                props.setDebitHasErrors(hasFieldError);
            }
            // errors on submit may not trigger onError()
            if (hasFieldError && event.details.length > 1) {
                onError({} as AciErrorObject);
            }
            checkForPushdowns();
        };

        // The callback triggered when there is an error in the funding account creation process.
        const onError = async (errorEvent: AciErrorObject) => {
            attempts.current += 1;
            if (getAcquisitionFlow() === AcquisitionFlow.PQ || getAcquisitionFlow() === AcquisitionFlow.PHA) {
                let isApplicationRedirecting = false;
                const applicationID = SessionStorage.get('ApplicationId');
                callAcquisitionBff<IAciFundingAttempt>('debit-card-funding-attempt', {
                    replace: { 'applicationId': applicationID },
                    body: {
                        correlationId: UUIDv4(),
                        requestDateTime: Math.floor(new Date().getTime() / 1000),
                        requestedBy: 'Retry',
                        channel: 'WEB',
                        ipAddress: await getIpAddress(),
                    },
                }).then(async (response) => {
                    if (!response) {
                        throw { response };
                    }

                    const responseData = response.data;
                    if (responseData.achAttemptsExceeded && responseData.debitAttemptsExceeded) {
                        redirect(false, props.attemptsExceededRedirect);
                    }

                    if (responseData.debitAttemptsExceeded) {
                        redirect(true, props.attemptsExceededRedirect);
                    }
                }).catch((err) => {
                    // redirect to pending-decision since there are no ACH attempts at this point
                    // user can technically retry ACI from application-status if the app is not declined
                    // and they still have ACI attempts
                    console.error(err);
                    redirect(false, props.fundingErrorRedirect);
                    isApplicationRedirecting = true;
                }).finally(() => {
                    isSubmitting.current = false;
                    if (props.singlePageFunding) {
                        props.isFundingSubmitting.current = false;
                        props.setIsSpinning(false);
                    }
                    if (!isApplicationRedirecting) {
                        setIsSpinning(false);
                    }
                    setIsDisabled(true);
                    if (props.singlePageFunding) {
                        props.setDebitHasErrors(true)
                    }
                });
            } else if (!props?.author && errorEvent?.message?.code === 'E.751.5001') {
                redirect(props.unprocessableEntityErrorCode, props.debitCardFailedPagePath);
            } else if (!props?.author && attempts?.current >= 3) {
                redirect(props.verificationErrorCode);
            }
        };
        
        // The callback triggered after data is retrieved, but before validating and creating the funding account.
        const beforeCreateToken = (event: any) => {
            event.data['single-use'] = true;
        };

        function createScriptElementAndInject(src) {
            // Script should only be injected once on mount
            // Guard protects against strictMode in development or multiple components on the same page
            const existingScript: HTMLElement = document.querySelector(`script[src='${src}']`);

            if (existingScript) {
                return new Promise<void>((resolve, reject) => {
                    aciScriptElementRef.current = existingScript;
                    if (existingScript.dataset.loaded) {
                        return resolve();
                    }
                    existingScript.addEventListener('load', () => resolve());
                    existingScript.addEventListener('error', reject);
                });
            }

            return new Promise<void>((resolve, reject) => {
                const aciScript = document.createElement('script');
                aciScript.setAttribute('data-aci-speedpay', '');
                aciScript.integrity = 'sha256-czr8oOHgAoQF57lASjkWgFFhAlXEitNEHVDrzJJXVbQ= sha384-iI5pWcq9qyfSO1CkU45BlOnaziUJeQ/MTf3zvf9rVuc7pCAW3+aJA0t7SaS5883E sha512-Kvjf+N9wjHiAIBUaYKapsu780LGsF3GwfWFguZWxxMGnEOudmOeqQLtFp6q+zHQpGA4okyMQ5XsxEbbs+gMm3Q==';
                aciScript.crossOrigin = 'anonymous';
                aciScript.referrerPolicy = 'strict-origin';

                if (!props.isInEditor && !props.author) {
                    aciScript.src = src;
                } else {
                    setIsSpinning(false);
                }

                aciScript.onload = () => {
                    aciScript.dataset.loaded = 'true';
                    resolve();
                };
                aciScript.onerror = reject;
                aciScriptElementRef.current = aciScript;
                document.body.appendChild(aciScript);
            });
        }

        const authenticateAci = async () => {
            // Authentication only ever happens once per mount
            const response = await acquisitionApiRequest<IAuthenticateAciResponse>('authenticate-aci', { method: 'GET' });
            if (response.status !== 200) {
                throw new Error('Could not authenticate ACI');
            }

            aciAuthenticationRef.current = {
                accessToken: response.data.access_token,
                billingAccountId: response.data.billing_account_id
            };
        }

        const initializeAci = async () => {
            const aciKey = getApiToken('aciKey');

            if (!aciKey) {
                throw new Error('Error validating ACI', { cause: 'aciKey' });
            }

            await (window as any).aci.speedpay.fundingAccountGateway.init(
                {
                    apiAuthKey: aciKey,
                    accessToken: aciAuthenticationRef.current.accessToken,
                    billerAccountId: aciAuthenticationRef.current.billingAccountId,
                    paymentMethod: 'Card',
                    userInterface: {
                        iframe: {
                            height: '32px',
                        },
                        input: {
                            fontfamily: 'Open Sans, Helvetica Neue, Helvetica, Helvetica, Arial, sans-serif',
                            fontsize: '13.3px',
                            border: '1px solid #cccccc',
                            padding: '5px 8px',
                            color: 'rgb(0, 0, 0, 0.75)',
                            lineheight: '18.75px',
                            width: {
                                all: 'calc(100% - 18px)',
                            },
                            focus: {
                                boxshadow: 'inset 0 1px 2px rgb(0, 0, 0, 0.1)',
                                border: '2px solid #076191',
                            },
                            valid: {
                                boxshadow: 'inset 0 1px 2px rgb(0, 0, 0, 0.1)',
                            },
                            invalid: {
                                boxshadow: 'inset 0 1px 2px rgb(0, 0, 0, 0.1)',
                            },
                        },
                    },
                },
                window.onValidate = onValidate,
                window.onCreateToken = onCreateToken,
                window.onError = onError,
                window.beforeCreateToken = beforeCreateToken,
            ).then((handler) => {
                // init() is a promise that should return a handler object
                // handler can be used to directly communicate with the SDK
                if (!handler) {
                    throw new Error("ACI did not return handler");
                }
            }).catch((err: Error) => {
                console.error(err.message);
                throw new Error('Error validating ACI', { cause: 'aciKey' });
            });
        }

        const setupAci = async () => {
            if (props.author || props.isInEditor) {
                setIsSpinning(false);
                return;
            }

            if (!window.aci?.speedpay?.fundingAccountGateway) {
                throw new Error('ACI does not exist');
            }
            
            // server-side authentication request
            await authenticateAci();
            // ACI script initialization and configuration
            await initializeAci();
        }

        createScriptElementAndInject(src)
            .then(async () => {
                // script element is injected and 'load' event has been called
                // only every invokes once per mount
                await setupAci();
            })
            .catch((err: Error) => {
                console.error(err.message);
                if (err?.cause && err.cause === 'aciKey') {
                    // go back to funding with ACH only
                    // if no attempts left, re-redirect to pending-decision
                    redirect(props.verificationErrorCode!)
                } else {
                    // go back to funding with ACH only
                    // if no attempts left, re-redirect to pending-decision
                    
                    if(window.location.search.indexOf('fundVariant=debitCardEnabled') > -1 || props.variant === 'debitCardEnabled') {
                        redirect(false, props.fundingErrorRedirect);
                    } else {
                        redirect(props.processingErrorCode!)
                    }
                }
                isApplicationRedirecting = true;
            })
            .finally(() => {
                isSubmitting.current = false;
                if (!isApplicationRedirecting) {
                    setIsSpinning(false);
                }
                // button starts disabled after script is 100% ready
            });
        
        return (() => {
            aciScriptElementRef.current.remove();
            delete window.onValidate;
            delete window.onCreateToken;
            delete window.onError;
            delete window.beforeCreateToken;
        });
    }, []);

    return (
        <Section id='debit-card-funding' className='DebitCardFunding'>
            {isSpinning ? <AcquisitionsLoader processingSteps={[]} /> : <></>}
            {/* 
                Fields must be visible and available to the ACI SDK script for initiation.
                This requires the fields to always be rendered in the DOM.
                Fields become visible after all script injection and initialization is complete.
            */}
            <div className={isSpinning ? 'hidden' : 'block'}>
                <Title className='DebitCardFunding__title'>{transformHTMLParserLinks(props.title!)}</Title>
                <div className='DebitCardFunding__text'>
                    <p>{transformHTMLParserLinks(props.instructionText!)}</p>
                    <Image loading='lazy' src={cardProviders} />
                </div>
                <Row className='DebitCardFunding__form'>
                    <Col xs={12} md={4}>
                        <div className='DebitCardFunding__col'>
                            <Label>{transformHTMLParserLinks(props.nameLabel!)}</Label>
                            <input className='DebitCardFunding__aciInput'
                                data-aci-speedpay='account-holder-name' id='acc-name' />
                        </div>
                        <div className='DebitCardFunding__col'>
                            <Label>{transformHTMLParserLinks(props.cardNumberLabel!)}</Label>
                            <div id='aci-card-number' className='DebitCardFunding__aci--solo'
                                data-aci-speedpay='card-number'></div>
                        </div>
                        <Row className='DebitCardFunding__groupedRow'>
                            <Col xs={7} className='DebitCardFunding__col'>
                                <Label>{transformHTMLParserLinks(props.cardExpirationLabel!)}</Label>
                                <div data-aci-speedpay='expiration-date'></div>
                            </Col>
                            <Col xs={5} className='DebitCardFunding__col--small'>
                                <Label>{transformHTMLParserLinks(props.cardSecurityLabel!)}</Label>
                                <div data-aci-speedpay='security-code'></div>
                            </Col>
                        </Row>
                        <Row className='DebitCardFunding__groupedRow'>
                            <Col xs={7} className='DebitCardFunding__col'>
                                <Label>{transformHTMLParserLinks(props.stateLabel!)}</Label>
                                <select data-aci-speedpay='account-region-code'
                                    defaultValue=''
                                    className='DebitCardFunding__aciInput--group DebitCardFunding__aciInput'>
                                    <option disabled value=''>Select</option>
                                    <option value='AL'>Alabama</option>
                                    <option value='AK'>Alaska</option>
                                    <option value='AZ'>Arizona</option>
                                    <option value='AR'>Arkansas</option>
                                    <option value='CA'>California</option>
                                    <option value='CO'>Colorado</option>
                                    <option value='CT'>Connecticut</option>
                                    <option value='DC'>DC - Washington DC</option>
                                    <option value='DE'>Delaware</option>
                                    <option value='FL'>Florida</option>
                                    <option value='GA'>Georgia</option>
                                    <option value='HI'>Hawaii</option>
                                    <option value='ID'>Idaho</option>
                                    <option value='IL'>Illinois</option>
                                    <option value='IN'>Indiana</option>
                                    <option value='IA'>Iowa</option>
                                    <option value='KS'>Kansas</option>
                                    <option value='KY'>Kentucky</option>
                                    <option value='LA'>Louisiana</option>
                                    <option value='ME'>Maine</option>
                                    <option value='MD'>Maryland</option>
                                    <option value='MA'>Massachusetts</option>
                                    <option value='MI'>Michigan</option>
                                    <option value='MN'>Minnesota</option>
                                    <option value='MS'>Mississippi</option>
                                    <option value='MO'>Missouri</option>
                                    <option value='MT'>Montana</option>
                                    <option value='NE'>Nebraska</option>
                                    <option value='NV'>Nevada</option>
                                    <option value='NH'>New Hampshire</option>
                                    <option value='NJ'>New Jersey</option>
                                    <option value='NM'>New Mexico</option>
                                    <option value='NY'>New York</option>
                                    <option value='NC'>North Carolina</option>
                                    <option value='ND'>North Dakota</option>
                                    <option value='OH'>Ohio</option>
                                    <option value='OK'>Oklahoma</option>
                                    <option value='OR'>Oregon</option>
                                    <option value='PA'>Pennsylvania</option>
                                    <option value='RI'>Rhode Island</option>
                                    <option value='SC'>South Carolina</option>
                                    <option value='SD'>South Dakota</option>
                                    <option value='TN'>Tennessee</option>
                                    <option value='TX'>Texas</option>
                                    <option value='UT'>Utah</option>
                                    <option value='VT'>Vermont</option>
                                    <option value='VA'>Virginia</option>
                                    <option value='WA'>Washington</option>
                                    <option value='WV'>West Virginia</option>
                                    <option value='WI'>Wisconsin</option>
                                    <option value='WY'>Wyoming</option>
                                </select>
                            </Col>
                            <Col xs={5} className='DebitCardFunding__col--small'>
                                <Label>{transformHTMLParserLinks(props.zipLabel!)}</Label>
                                <input className='DebitCardFunding__aciInput DebitCardFunding__aciInput--group'
                                    data-aci-speedpay='account-postal-code' maxLength={5} />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                {!props.singlePageFunding && (
                    <input className='Button DebitCardFunding__submitButton' 
                        type='button' 
                        data-aci-speedpay='card-submit-button' 
                        disabled={isDisabled}
                        onClick={submitOnClick}
                        value={props.submitButtonText}>
                    </input>
                )
                }
            </div>
        </Section>
    );
};

export default DebitCardFunding;